import { motion } from 'framer-motion';
import { FiArrowUpRight, FiLayers } from 'react-icons/fi';
import SectionHeading from './SectionHeading';
import { SiteConfig } from '../types/portfolio';
import Icon from './Icon';

type ProjectsGridProps = {
  projects: SiteConfig['projects'];
};

function ProjectsGrid({ projects }: ProjectsGridProps) {
  return (
    <section id="projects" className="section-shell">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <SectionHeading
          eyebrow="Projects"
          title="Representative enterprise work spanning healthcare and legal platforms."
          description="Each project reflects delivery in business-critical environments where backend scale, integration quality, and usable interfaces matter."
        />

        <div className="grid gap-6 lg:grid-cols-2">
          {projects.map((project, index) => (
            <motion.article
              key={project.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.5, delay: index * 0.08 }}
              className="glass-panel rounded-[2rem] p-8"
            >
              <div className="flex items-center justify-between gap-3">
                <span className="inline-flex items-center gap-2 rounded-full border border-sky-400/20 bg-sky-400/10 px-4 py-2 text-xs font-semibold uppercase tracking-[0.25em] text-sky-200">
                  <Icon icon={FiLayers} />
                  Enterprise Project
                </span>
                <Icon icon={FiArrowUpRight} className="text-slate-500" />
              </div>

              <h3 className="mt-6 text-2xl font-semibold text-white">{project.title}</h3>
              <p className="mt-4 text-base leading-8 text-slate-300">{project.description}</p>

              {project.architecture ? (
                <div className="mt-6 rounded-2xl border border-teal-400/15 bg-teal-400/10 px-4 py-4 text-sm leading-7 text-teal-100">
                  <span className="font-semibold text-white">Backend architecture:</span>{' '}
                  {project.architecture}
                </div>
              ) : null}

              {project.features ? (
                <div className="mt-6">
                  <p className="text-sm font-semibold uppercase tracking-[0.25em] text-slate-400">
                    Key features
                  </p>
                  <ul className="mt-4 grid gap-3 sm:grid-cols-2">
                    {project.features.map((feature) => (
                      <li
                        key={feature}
                        className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-slate-200"
                      >
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}

              <div className="mt-6 flex flex-wrap gap-3">
                {project.technologies.map((tech) => (
                  <span
                    key={tech}
                    className="rounded-full border border-white/10 bg-slate-900/80 px-3 py-2 text-xs font-medium uppercase tracking-[0.18em] text-slate-200"
                  >
                    {tech}
                  </span>
                ))}
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}

export default ProjectsGrid;
